// One keystroke to anywhere, and to a set.
//
// The bill is two hundred sets across three days. A menu of five destinations
// reaches every screen and no artist, so this reaches both: a page by name, a
// day by name, an act by name, with the act's own set added to the plan from
// the same keystroke that found it.
//
// Nothing here holds state that matters. Closing it loses a query string.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { fmtTime } from '../../engine/time.ts';

interface Item {
  id: string;
  group: string;
  label: string;
  sub: string;
  hint?: string;
  run: () => void;
}

const PAGES: { to: string; label: string; sub: string }[] = [
  { to: '/home', label: 'Home', sub: 'Everything you have started' },
  { to: '/plan', label: 'My plan', sub: 'The stage grid and your day' },
  { to: '/plan/improve', label: 'Improve my plan', sub: 'The choices worth making next' },
  { to: '/lineup', label: 'The lineup', sub: 'Every artist, rate what you know' },
  { to: '/preferences', label: 'Festival preferences', sub: 'Taste, energy, who the plan is for' },
  { to: '/event', label: 'Event mode', sub: 'What is on right now, and the walk' },
  { to: '/weather', label: 'Weather and travel', sub: 'Alerts, radar, forecast and departure updates' },
  { to: '/news', label: 'News and updates', sub: 'Latest reports, key updates, earlier weather warnings' },
  { to: '/art', label: "Artists' corner", sub: 'Outfits, kandi, tattoos, drawings' },
  { to: '/art/contributors', label: 'Contributing artists', sub: 'Work used in the site' },
  { to: '/photos', label: 'Photos', sub: 'Your shelf and what was shared' },
  { to: '/forum', label: 'Forum', sub: 'Threads and replies' },
  { to: '/messages', label: 'Messages', sub: 'Your crew' },
  { to: '/search', label: 'Find people', sub: 'Crews open to new members' },
  { to: '/group', label: 'Group view', sub: 'Where a crew disagrees' },
  { to: '/conflicts', label: 'Group choices', sub: 'Open questions in the crew' },
  { to: '/priorities', label: 'Priorities', sub: 'The artists you will not miss' },
  { to: '/profile', label: 'Profile', sub: 'Your page' },
  { to: '/settings', label: 'Settings', sub: 'Theme, density, art and your data' },
  { to: '/feedback', label: 'Send feedback', sub: 'Tell the site what is wrong' },
  { to: '/faq', label: 'Help', sub: 'How the planner works' },
];

/** Prefix beats contained, and a shorter label beats a longer one. */
function score(haystack: string, needle: string): number {
  const text = haystack.toLowerCase();
  const index = text.indexOf(needle);
  if (index < 0) return -1;
  return (index === 0 ? 0 : 100) + index + text.length / 400;
}

export function CommandPalette() {
  const { state, derived, actions } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const field = useRef<HTMLInputElement>(null);
  const listbox = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setCursor(0);
  }, []);

  // The opening keystroke is global. The typing keystrokes are not, so a person
  // writing a forum post never loses a character to this.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((was) => !was);
        return;
      }
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    const onOpen = () => setOpen(true);
    window.addEventListener('lostlands:palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('lostlands:palette', onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  // Sets are indexed once per bill change rather than per keystroke.
  const acts = useMemo(() => {
    const byArtist = new Map<string, { artist: string; sets: typeof state.sets }>();
    for (const set of state.sets) {
      const artist = derived.artistById.get(set.artistId);
      if (!artist) continue;
      const found = byArtist.get(artist.id);
      if (found) found.sets.push(set);
      else byArtist.set(artist.id, { artist: artist.name, sets: [set] });
    }
    return [...byArtist.entries()].map(([id, entry]) => ({ id, ...entry }));
  }, [state.sets, derived.artistById]);

  const items = useMemo<Item[]>(() => {
    const needle = query.trim().toLowerCase();
    const out: { item: Item; rank: number }[] = [];

    for (const page of PAGES) {
      const rank = needle ? score(`${page.label} ${page.sub}`, needle) : 0;
      if (rank < 0) continue;
      out.push({
        rank,
        item: {
          id: `go:${page.to}`,
          group: 'Go to',
          label: page.label,
          sub: page.sub,
          run: () => navigate(page.to),
        },
      });
    }

    for (const ctx of derived.dayContexts) {
      const picked = derived.picksByDay.get(ctx.day.id)?.length ?? 0;
      const rank = needle ? score(`${ctx.day.label} day plan schedule`, needle) : 0;
      if (rank < 0) continue;
      out.push({
        rank,
        item: {
          id: `day:${ctx.day.id}`,
          group: 'Days',
          label: ctx.day.label,
          sub: `${ctx.sets.length} sets, ${picked} picked`,
          hint: 'open the grid',
          run: () => navigate(`/plan?day=${encodeURIComponent(ctx.day.id)}`),
        },
      });
    }

    if (needle.length > 0) {
      for (const act of acts) {
        const rank = score(act.artist, needle);
        if (rank < 0) continue;
        const set = act.sets[0];
        const day = state.days.find((d) => d.id === set.dayId);
        const stage = derived.stageById.get(set.stageId);
        const already = derived.pickedSetIds.has(set.id);
        out.push({
          rank: rank - 30,
          item: {
            id: `act:${act.id}`,
            group: 'On the bill',
            label: act.artist,
            sub: `${day?.label ?? 'that day'} · ${stage?.name ?? 'that stage'} · ${fmtTime(set.startTs)}${
              act.sets.length > 1 ? ` · +${act.sets.length - 1} more` : ''
            }`,
            hint: already ? 'remove from my plan' : 'add to my plan',
            run: () => {
              void actions.togglePick(set.id);
              navigate(`/plan?day=${encodeURIComponent(set.dayId)}`);
            },
          },
        });
      }
    }

    return out
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 14)
      .map((entry) => entry.item);
  }, [query, acts, derived, state.days, navigate, actions]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    listbox.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  if (!open) return null;

  const choose = (item: Item) => {
    close();
    item.run();
  };

  let lastGroup = '';

  return (
    <div className="paletteveil" onMouseDown={close} role="presentation">
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Jump to anything"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <input
          ref={field}
          className="palettefield"
          value={query}
          placeholder="A page, a day, or an artist by name"
          aria-label="Search pages, days and artists"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-results"
          aria-activedescendant={items[cursor] ? `palette-${items[cursor].id}` : undefined}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              close();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCursor((c) => (items.length === 0 ? 0 : (c + 1) % items.length));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCursor((c) => (items.length === 0 ? 0 : (c - 1 + items.length) % items.length));
            } else if (e.key === 'Enter' && items[cursor]) {
              e.preventDefault();
              choose(items[cursor]);
            }
          }}
        />

        <div className="paletteresults" id="palette-results" role="listbox" ref={listbox}>
          {items.length === 0 && (
            <p className="muted" style={{ margin: '10px 4px' }}>
              Nothing by that name. Artists match on the name printed on the bill.
            </p>
          )}
          {items.map((item, index) => {
            const heading = item.group !== lastGroup ? item.group : '';
            lastGroup = item.group;
            return (
              <div key={item.id}>
                {heading && <p className="palettegroup">{heading}</p>}
                <button
                  type="button"
                  id={`palette-${item.id}`}
                  role="option"
                  aria-selected={index === cursor}
                  className={`paletterow ${index === cursor ? 'on' : ''}`}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => choose(item)}
                >
                  <span>
                    {item.label}
                    <span className="sub" style={{ display: 'block' }}>{item.sub}</span>
                  </span>
                  {item.hint && <span className="palettehint">{item.hint}</span>}
                </button>
              </div>
            );
          })}
        </div>

        <p className="palettefoot">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> to move
          </span>
          <span>
            <kbd>enter</kbd> to open
          </span>
          <span>
            <kbd>esc</kbd> to close
          </span>
        </p>
      </div>
    </div>
  );
}

/** Opens the palette from a control that is not a keystroke. */
export function openPalette(): void {
  window.dispatchEvent(new Event('lostlands:palette'));
}
